import React from "react"
import { Box, Typography } from "@mui/material"
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined"
import BlockIcon from "@mui/icons-material/Block"
import { waypointZones } from "@/utils/routeHelper"
import { legIssues } from "@/features/route/legAlerts.js"
import { LEG_ISSUE_ICONS } from "@/features/route/LegCardBody.jsx"
import {
  INK,
  MUTED,
  ORANGE,
  RED,
  SMALL_LABEL_SX,
  fmt,
} from "@/features/route/alertCardParts.jsx"
import KpiTile, { limitTone } from "@/components/ui/KpiTile"
import { CardHero, CourseTile, DetailsToggle, finite, tileRow } from "@/components/map/InfoCardParts"
import { CARD_TITLE_SX, FIELD_LABEL_SX, ROW_DESCRIPTION_SX } from "@/theme/panelStyles"
import { DirectionArrow, flowDeg, formatWeatherValue as formatValue, waypointWeather as weatherOf } from "@/features/route/waypointWeather.jsx"
import { useAccessContext } from "@/contexts/AccessContext"

const ZONE_LABELS = {
  eca: "ECA",
  high_risk: "High risk area",
  war_risk: "War risk area",
  operational_risk: "Operational risk area",
}

const WEATHER_TILES = [
  { param: "wave", label: "Wave", unit: "m", limitKey: "waveLimit" },
  { param: "wind", label: "Wind", unit: "kn", limitKey: "windLimit" },
  { param: "current", label: "Current", unit: "kn", limitKey: "currentLimit" },
]


function WeatherTile({ tile, value, limits }) {
  const { param, label, unit, limitKey } = tile
  const speed = value?.speed ?? null
  const dir = speed != null ? value?.dir ?? null : null
  const exceeded = limitTone(speed, finite(limits?.[limitKey]), (v) => formatValue(v, param))
  const soft = exceeded.tone ? RED.color : MUTED
  return (
    <KpiTile
      label={label}
      value={speed == null ? null : formatValue(speed, param)}
      unit={unit}
      adornment={
        dir != null ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <DirectionArrow deg={flowDeg(param, dir)} color={soft} />
            <Typography sx={{ fontSize: ROW_DESCRIPTION_SX.fontSize, fontWeight: 600, color: soft }}>{Math.round(dir)}°</Typography>
          </Box>
        ) : null
      }
      subValue={speed == null ? "No forecast" : null}
      {...exceeded}
      // Name, arrow and degrees share one row in a third of the card.
      sx={{ px: "6px" }}
    />
  )
}

// One compact tinted row: zone at this point or a warning of the outgoing leg.
function AlertRow({ Icon, palette, label, text, chip }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        p: "6px 8px",
        borderRadius: "10px",
        background: palette.tint,
        minWidth: 0,
      }}
    >
      <Icon sx={{ fontSize: 16, color: palette.color, flex: "none" }} />
      <Typography sx={{ ...SMALL_LABEL_SX, color: palette.color, flex: "none" }}>{label}</Typography>
      <Typography
        sx={{ ...CARD_TITLE_SX, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={text ?? undefined}
      >
        {text}
      </Typography>
      {chip && (
        <Typography
          component="span"
          sx={{
            flex: "none",
            height: 18,
            px: 0.75,
            borderRadius: "9px",
            display: "inline-flex",
            alignItems: "center",
            fontSize: FIELD_LABEL_SX.fontSize,
            fontWeight: 700,
            color: "white",
            background: palette.color,
          }}
        >
          {chip}
        </Typography>
      )}
    </Box>
  )
}

function issueText(issue) {
  if (issue.kind === "max_speed" || issue.kind === "min_speed")
    return `${issue.kind === "max_speed" ? "max" : "min"} ${fmt(issue.limit, "kn")} kn`
  if (issue.value != null) return `${fmt(issue.value, issue.unit)} ${issue.unit}`
  // The label already says it; the leg card's sentence would only be cut off here.
  return issue.kind === "closed_viaarea" ? null : issue.detail
}

export default function WaypointCardContent({ feature, routeType, sketch = false, limits = null, color = INK }) {
  const { accessContext } = useAccessContext()
  // Same gate as the RNW Analyser: only those users get the raw property dump.
  const canSeeRawDetails = !!accessContext?.uiCapabilities?.canSeeRnwAnalyser
  const props = feature?.properties ?? {}
  const coords = feature?.geometry?.coordinates

  const sog = finite(
    props.sog ?? props.albis_sensors?.speedOverGround_kn ?? props.albis_reports?.speedOverGround_kn,
  )
  const heading = finite(props.heading ?? props.albis_sensors?.headingTrue_deg)
  const cog = finite(props.course_bearing)
  // Only reported waypoints have a heading sensor; planned ones only have a course.
  const showHeading = routeType === "reported"
  const motionCount = [sog, cog, showHeading ? heading : null].filter((v) => v != null).length

  const weather = weatherOf(props)
  const hasWeather = WEATHER_TILES.some((tile) => weather[tile.param]?.speed != null)
  // Current stays visible next to wind/wave: its forecast ends days earlier, so a gap is expected, not an error.
  const weatherTiles =
    sketch || !hasWeather
      ? []
      : WEATHER_TILES.filter((tile) => tile.param === "current" || weather[tile.param]?.speed != null)
  const zones = waypointZones(props.warnings)
  const issues = legIssues(props)

  return (
    <>
      <CardHero label="ETA" timestamp={sketch ? null : props.timestamp} coords={coords} />

      {motionCount > 0 && (
        <Box sx={tileRow(motionCount)}>
          {sog != null && <KpiTile label="SOG" value={sog.toFixed(1)} unit="kn" />}
          {cog != null && <CourseTile label="COG" deg={cog} color={color} />}
          {showHeading && heading != null && <CourseTile label="HDG" deg={heading} color={color} />}
        </Box>
      )}

      {weatherTiles.length > 0 && (
        <Box sx={tileRow(weatherTiles.length)}>
          {weatherTiles.map((tile) => (
            <WeatherTile key={tile.param} tile={tile} value={weather[tile.param]} limits={limits} />
          ))}
        </Box>
      )}

      {(zones.length > 0 || issues.length > 0) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {zones.map((z) => (
            <AlertRow
              key={`${z.kind}-${z.name}`}
              Icon={GppMaybeOutlinedIcon}
              palette={z.kind === "eca" ? ORANGE : RED}
              label={ZONE_LABELS[z.kind]}
              text={z.name}
              chip={z.detail}
            />
          ))}
          {issues.map((issue) => (
            <AlertRow
              key={issue.kind}
              Icon={LEG_ISSUE_ICONS[issue.kind] ?? BlockIcon}
              palette={issue.level === "warning" ? RED : ORANGE}
              label={issue.title}
              text={issueText(issue)}
            />
          ))}
        </Box>
      )}

      {canSeeRawDetails && !sketch && (
        <DetailsToggle>
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,79,125,0.25)", borderRadius: 3 },
            }}
          >
            {flattenWaypointProps(props).map(([key, value]) => (
              <Box key={key} sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.35 }}>
                <Typography sx={{ fontSize: 11, color: MUTED, wordBreak: "break-all" }}>{key}</Typography>
                <Typography sx={{ fontSize: 11, color: INK, fontWeight: 600, textAlign: "right" }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </DetailsToggle>
      )}
    </>
  )
}

// Flatten waypoint properties into [label, value] rows, recursing one level
// into nested objects so blobs like weather_snapshot / rnw_evaluation /
// warnings show up as `weather_snapshot.wind_gfs_speed_knots_interp` etc.
// Skips empty values, internal helpers and very large nested arrays.
function flattenWaypointProps(props) {
  if (!props || typeof props !== "object") return []
  const SKIP_KEYS = new Set([
    // already shown explicitly in the header section above the expander
    "timestamp",
    "datetime_utc",
    "name",
    "number",
    "extended_number",
    "waypoint_number",
    "waypoint_subnumber",
  ])
  const rows = []
  const push = (label, value) => {
    if (value === null || value === undefined || value === "") return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      rows.push([label, value.join(", ")])
      return
    }
    if (typeof value === "object") return // handled by recursion
    if (typeof value === "number") {
      rows.push([
        label,
        Number.isInteger(value) ? String(value) : Number(value).toFixed(2),
      ])
      return
    }
    rows.push([label, String(value)])
  }

  for (const [key, value] of Object.entries(props)) {
    if (SKIP_KEYS.has(key)) continue
    if (value && typeof value === "object" && !Array.isArray(value)) {
      // Recurse one level into JSONB blobs
      for (const [subKey, subValue] of Object.entries(value)) {
        push(`${key}.${subKey}`, subValue)
      }
    } else {
      push(key, value)
    }
  }
  rows.sort(([a], [b]) => a.localeCompare(b))
  return rows
}
